import React, { useEffect, useRef } from "react";
import {
  Loader2,
  AlertCircle,
  Check,
  RotateCcw,
  X,
  Images,
  Video,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { ListingPhotoQueueItem } from "@/hooks/useListingPhotoUploadQueue";
import { photoSessionTheme as theme, photoSessionStrip } from "./photoSessionTheme";
import { isVideoMedia } from "@/lib/isVideoMedia";

export type StripDisplayItem = Omit<ListingPhotoQueueItem, "previewUrl"> & {
  previewUrl?: string;
  isOptimistic?: boolean;
};

export interface PhotoSessionStripLabels {
  statusPending?: string;
  statusUploading?: string;
  statusDone?: string;
  statusFailed?: string;
  removePhoto?: string;
  viewPhoto?: string;
  retry?: string;
  emptyPlaceholder?: string;
}

interface PhotoSessionThumbnailStripProps {
  items: StripDisplayItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  labels?: PhotoSessionStripLabels;
  layout?: "horizontal" | "vertical";
}

function ThumbnailTile({
  item,
  isSelected,
  onSelect,
  onRemove,
  onRetry,
  labels,
}: {
  item: StripDisplayItem;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  labels: PhotoSessionStripLabels;
}) {
  const isOptimistic = Boolean(item.isOptimistic);
  const isVideoItem = Boolean(
    item.previewUrl && isVideoMedia(item.blob?.type, item.previewUrl),
  );
  const canRemove = item.status === "pending" && !isOptimistic;
  const showSpinner =
    isOptimistic ||
    item.status === "pending" ||
    item.status === "uploading";

  return (
    <div
      className={cn(
        "relative shrink-0 rounded-lg overflow-hidden ring-2 transition-all",
        isSelected ? "ring-[#f59e0b] ring-offset-2 ring-offset-black" : "ring-white/20",
        isOptimistic && "animate-pulse",
      )}
      style={{
        width: photoSessionStrip.tileSize,
        height: photoSessionStrip.tileSize,
      }}
    >
      <button
        type="button"
        className="relative w-full h-full touch-manipulation cursor-pointer"
        onClick={() => {
          if (item.previewUrl) onSelect(item.id);
        }}
        disabled={!item.previewUrl}
        aria-pressed={isSelected}
        aria-label={labels.viewPhoto ?? "View photo"}
      >
        {item.previewUrl ? (
          <>
            {isVideoItem ? (
              <video
                src={item.previewUrl}
                muted
                playsInline
                preload="metadata"
                className="w-full h-full object-cover pointer-events-none"
              />
            ) : (
              <img
                src={item.previewUrl}
                alt=""
                className="w-full h-full object-cover"
                draggable={false}
              />
            )}
            {isVideoItem && (
              <div
                className="absolute top-1 left-1 h-5 w-5 rounded-full flex items-center justify-center shadow-md pointer-events-none"
                style={{ backgroundColor: "rgba(0,0,0,0.7)", color: "#fff" }}
                aria-hidden
              >
                <Video className="h-3 w-3" />
              </div>
            )}
            {showSpinner && (
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
              >
                <Loader2
                  className="h-6 w-6 animate-spin"
                  style={{ color: theme.accent }}
                />
              </div>
            )}
          </>
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: theme.surfaceMuted }}
          >
            <Loader2
              className="h-6 w-6 animate-spin"
              style={{ color: theme.textMuted }}
            />
          </div>
        )}
      </button>

      <div
        className="absolute bottom-0 inset-x-0 flex items-center justify-center py-0.5"
        style={{ backgroundColor: "rgba(0,0,0,0.65)" }}
      >
        {showSpinner && item.status !== "done" && item.status !== "failed" && (
          <Loader2
            className="h-3.5 w-3.5 animate-spin"
            style={{
              color:
                item.status === "uploading" ? theme.accent : theme.textMuted,
            }}
          />
        )}
        {item.status === "done" && !isOptimistic && (
          <Check className="h-3.5 w-3.5" style={{ color: theme.success }} />
        )}
        {item.status === "failed" && !isOptimistic && (
          <button
            type="button"
            className="flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              onRetry(item.id);
            }}
          >
            <AlertCircle className="h-3.5 w-3.5" style={{ color: theme.error }} />
          </button>
        )}
      </div>

      {canRemove && (
        <button
          type="button"
          className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full flex items-center justify-center shadow-md z-10 text-white"
          style={{ backgroundColor: theme.error }}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.id);
          }}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export function PhotoSessionThumbnailStrip({
  items,
  selectedId,
  onSelect,
  onRemove,
  onRetry,
  labels = {},
  layout = "horizontal",
}: PhotoSessionThumbnailStripProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(items.length);
  const isEmpty = items.length === 0;
  const isVertical = layout === "vertical";

  useEffect(() => {
    if (items.length <= prevCountRef.current) {
      prevCountRef.current = items.length;
      return;
    }
    prevCountRef.current = items.length;
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (isVertical) {
        el.scrollTop = el.scrollHeight;
      } else {
        el.scrollLeft = el.scrollWidth;
      }
    });
  }, [items.length, isVertical]);

  const stopTouchBubble = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className={cn(
        "shrink-0 flex flex-col min-h-0 self-stretch",
        isVertical ? "border-r" : "border-t",
      )}
      style={{
        backgroundColor: theme.surface,
        borderColor: theme.border,
        ...(isVertical
          ? {
              width: photoSessionStrip.verticalWidth,
              minWidth: photoSessionStrip.verticalWidth,
            }
          : {
              width: "100%",
              height: photoSessionStrip.horizontalHeight,
              minHeight: photoSessionStrip.horizontalHeight,
            }),
      }}
    >
      {isEmpty ? (
        <div
          className={cn(
            "flex flex-1 items-center justify-center gap-2 p-3 min-h-0",
            isVertical && "flex-col",
          )}
          style={{ color: theme.textMuted }}
        >
          <Images className="h-4 w-4 shrink-0 opacity-60" />
          <p
            className={cn(
              "text-[10px] text-center leading-snug",
              isVertical ? "max-w-[4rem]" : "max-w-[11rem]",
            )}
          >
            {labels.emptyPlaceholder ??
              "Your captured photos will appear here"}
          </p>
        </div>
      ) : (
        <div
          ref={scrollRef}
          role="list"
          data-photo-session-scroll
          data-layout={isVertical ? "vertical" : "horizontal"}
          className={cn(
            "flex-1 min-h-0 min-w-0 gap-2 p-2",
            isVertical
              ? "flex flex-col flex-nowrap overflow-y-auto overflow-x-hidden"
              : "flex flex-row flex-nowrap overflow-x-auto overflow-y-hidden",
          )}
          style={{
            WebkitOverflowScrolling: "touch",
            touchAction: isVertical ? "pan-y" : "pan-x",
            overscrollBehavior: "contain",
          }}
          onTouchStart={stopTouchBubble}
          onTouchMove={stopTouchBubble}
        >
          {items.map((item) => (
            <ThumbnailTile
              key={item.id}
              item={item}
              isSelected={item.id === selectedId}
              onSelect={onSelect}
              onRemove={onRemove}
              onRetry={onRetry}
              labels={labels}
            />
          ))}
        </div>
      )}
    </div>
  );
}
